import {
  Badge,
  Button,
  FlexBox,
  Icon,
  Page,
  Table,
  Tag,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { ampli } from 'lib-shared-tracking';
import { useInterval } from 'react-use';

import { MimChatbotConfiguration, mimChatbotsApi } from 'apis/chatbots';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { ChatbotActionsMenu } from './components/chatbotActionMenu/chatbotActionsMenu';
import { ChatbotCreateModal } from './components/chatbotCreateModal/chatbotCreateModal';
import { ChatbotEditIdentityModal } from './components/chatbotEditIdentityModal/chatbotEditIdentityModal';
import styles from './settingsChatbotsMim.module.scss';

const emptyArray: MimChatbotConfiguration[] = [];

export const SettingsChatbotsMimView = (): JSX.Element => {
  const notify = useNotifications();
  const dispatch = useAppDispatch();

  const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const { chatbots, isLoading, isError, refetch } =
    mimChatbotsApi.useReadMimChatbotsQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        chatbots:
          data && Array.isArray(data.data.chatbots)
            ? [...data.data.chatbots].reverse()
            : emptyArray,
      }),
    });
  const [deleteChatbot] = mimChatbotsApi.useDeleteMimChatbotMutation();

  useInterval(
    () => refetch(),
    chatbots.some((chatbot) =>
      chatbot.knowledgeBases.some((kb) => kb.state !== 'ready'),
    )
      ? 20000
      : null,
  );

  const handleCreateChatbot = async () => {
    ampli.chatbotCreateModalOpened();

    await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:createChatbotModal',
          arg: { numberOfChatbots: chatbots.length },
        },
      }),
    );
  };

  const handleEditChatbot = async (chatbot: MimChatbotConfiguration) => {
    ampli.chatbotEditIdentityModalOpened();
    await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:editChatbotIdentityModal',
          arg: { chatbot },
        },
      }),
    );
  };

  const handleDeleteChatbot = async (chatbot: MimChatbotConfiguration) => {
    const name = chatbot.name || '';
    ampli.chatbotDeleteModalOpened();

    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Delete "${name}"`,
            description: `Are you sure you want to permanently delete the "${name}"? This action cannot be undone.`,
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        await deleteChatbot({ id: chatbot.id });
        ampli.chatbotDeleted();
        notify(
          {
            title: 'Success',
            text: `"${name}" has been deleted.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );
      } catch (error) {
        notify(
          {
            title: `Error while deleting "${name}".`,
            icon: 'exclamation',
            className: 'danger',
            time: 5000,
          },
          NotificationType.Error,
        );
      }
    }
  };

  return (
    <>
      <Page.Section
        title="Chatbots"
        titleMeta={
          <>
            {chatbots.length > 0 && (
              <Badge size="small" variant="secondary">
                {chatbots.length}
              </Badge>
            )}
          </>
        }
        controls={<Button onClick={handleCreateChatbot}>Create chatbot</Button>}
        className={styles.container}
      >
        <Table stickyHeader loading={isLoading} loadingFailed={isError}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th columnId="name">Internal reference</Table.Th>
              <Table.Th columnId="base">Data Source</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {chatbots.length > 0 ? (
              chatbots.map((chatbot, index) => (
                <Table.Tr key={index}>
                  <Table.Td>{chatbot?.name || ''}</Table.Td>
                  <Table.Td>
                    <FlexBox gap="100">
                      {chatbot.knowledgeBases?.map((kb) => (
                        <Tooltip content={capitalize(kb.state)} key={kb.id}>
                          <Tag
                            color={
                              kb.state === 'ready'
                                ? colors.green[400]
                                : colors.orange[400]
                            }
                          >
                            {kb.name}
                          </Tag>
                        </Tooltip>
                      ))}
                    </FlexBox>
                  </Table.Td>
                  <Table.Td align="right">
                    {chatbot.knowledgeBases?.every(
                      (kb) => kb.state === 'ready',
                    ) && (
                      <ChatbotActionsMenu
                        onEditChatbotIdentity={() => handleEditChatbot(chatbot)}
                        onDeleteChatbot={() => handleDeleteChatbot(chatbot)}
                      />
                    )}
                  </Table.Td>
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={4} className={styles.noData}>
                  <FlexBox
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap="300"
                  >
                    <Icon size={20} name="dixa" />
                    <Text.Paragraph variant="1">Mim chatbots</Text.Paragraph>
                    <Text.Paragraph variant="2">
                      To create a Mim chatbot you need to first add at least one
                      data source in the table below.
                    </Text.Paragraph>
                    <Button onClick={handleCreateChatbot}>
                      Create chatbot
                    </Button>
                  </FlexBox>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Page.Section>
      <ChatbotCreateModal />
      <ChatbotEditIdentityModal />
    </>
  );
};
